// M4-A shale (docs/M4_SPEC.md §1–§5): shared helpers — lazy state, gear & crew lookups, field memo, deterministic
// per-well draws. Nothing here runs before 2000: every caller is gated on rules.M4_FROM (or on well.lateral /
// prod.shale, which only exist from 2000).
import { M4_FROM } from '../../data/m4/rules.js';
import { plotTier, TIER_QI_MULT, streamsOf } from '../../data/m4/fields.js';
import { eqDef, fieldDef, stat, yf, eraDollars } from '../defs.js';
import { reservoirsAt } from '../geo.js';
import { hash32 } from '../../core/rng.js';
import { effects, hasUpgrade } from '../research.js';

// Sim-side balance knobs the content tables don't carry (documented in docs/CONTRACT.md "M4-A shale").
export const SHALE_SIM = {
  verticalQiMult: 0.13,        // a vertical well in a shale bench: ≈ 1,000 ft of effective lateral (qiRange is per 7,500 ft)
  minLateralFt: 500,
  standbyMult: 1.0,            // contracted frac fleets bill the full day rate whether pumping or not (keep them busy)
  flowbackDays: 14, drilloutTrips: 1,
  flowingDays: [180, 365],     // shale wells flow 6–12 months, then need artificial lift
  unliftedMult: 0.35,          // a loaded-up well without lift
  liftFailDowntime: 5, liftWorkoverCost: 60000, liftWorkoverBaseYear: 2014,
  gasSlugMult: 0.85,           // an ESP in a gassy well (streams.gas above its gasTolerance)
  ducReinspectCost: 300000, ducReinspectDays: 10, ducBaseYear: 2018,
  refracQiShare: 0.5,          // refrac: an incremental fresh curve of qi = ip30 × U(qiMultRange) × share on top of the old one
  refracFleetDays: 3, refracFlowbackDays: 7,
  inBasinTruckMi: 30, nwTruckMi: 20,
  fracCrewDiscountPer: 0.01, fracCrewDiscountMax: 0.15, wirelineOpDiscount: 0.2,
  quakeFeltRep: 0.5, waterTruckThirdPartyFee: 0.5, missingRolePenalty: 0.85,
  parentHitDays: 60, childLookbackBenchOnly: true,
  spotWaterTruckPerBbl: 2.0,
  fracFuelPerDay: 30000, fracFuelBaseYear: 2014, // diesel burned by a Tier-2 fleet on a pumping day (× def fuelMult)
  fracCo2TPerDay: 250,                             // t CO₂ per pumping day (× def emissionsMult) — booked for the ESG workstream
};

export const on = (state) => yf(state) >= M4_FROM;
export const isShaleFieldId = (fid) => !!fieldDef(fid)?.shale;

// Lazy M4-A state (JSON-safe). Created on first use (always ≥ 2000).
export function sh(state) {
  return (state.shale ??= {
    v: 1, auto: { sand: true, water: true, disposal: true, frac: true, lift: true },
    services: {}, water: {}, fields: {}, pools: {}, pads: {}, quakes: [], nextJob: 1,
  });
}
export const shOpt = (state) => state.shale ?? null;
export function fieldShale(state, fid) {
  const S = sh(state);
  return (S.fields[fid] ??= { day: -1, oilBbl: 0, gasMcf: 0, nglBbl: 0, waterBbl: 0, injected: 0, disposalCap: 0, curtail: 1, fracWaterToday: 0, quakes: 0, lastQuakeDay: null });
}

// Deterministic per-well uniform in [0,1) for geometry & flavour (does not touch state.rng).
export const wellHash = (state, well, key) => hash32((state.seed ?? 0) ^ hash32(0, well.id), key) / 4294967296;

// ---- gear & crew --------------------------------------------------------------------------------------------
export function gearDefs(state, rig) {
  const out = [];
  for (const uid of Object.values(rig?.gear ?? {})) { const it = uid && state.items[uid]; if (it) { const d = eqDef(it.defId); if (d) out.push(d); } }
  return out;
}
export const gearIn = (state, rig, slot) => { const uid = rig?.gear?.[slot]; const it = uid && state.items[uid]; return it ? eqDef(it.defId) : null; };
// 'rss' (rotary steerable) | 'motor' (steerable/directional motor) | null (cannot build a curve)
export function steerTool(state, rig) {
  if (!rig) return null;
  const s = gearIn(state, rig, 'steer');
  if (s && stat(s, 'rss', false)) return 'rss';
  const m = gearIn(state, rig, 'motor');
  if (m && stat(m, 'directional', false)) return 'motor';
  return null;
}
// 'lwd' | 'mwd' | 'none'
export function guidance(state, rig) {
  if (gearIn(state, rig, 'lwd')) return 'lwd';
  if (gearIn(state, rig, 'mwd')) return 'mwd';
  return 'none';
}
export const activeCrewOf = (state, role) => Object.values(state.crew).filter((c) => c.role === role && c.status === 'active' && !c.injuredUntil);
// Remote geosteering centre (from 2008) counts any hired geosteerer; before that one must be on the rig's crew.
export function hasGeosteerer(state, rig) {
  if (rig?.crewIds?.some((id) => { const c = state.crew[id]; return c?.role === 'geosteerer' && c.status === 'active' && !c.injuredUntil; })) return true;
  return yf(state) >= 2008 && activeCrewOf(state, 'geosteerer').length > 0;
}
export function completionSkillMult(state) {
  const best = activeCrewOf(state, 'completions_engineer').reduce((a, c) => Math.max(a, c.skill), -1);
  return 0.9 + 0.2 * (best < 0 ? 25 : best) / 100;
}
export const upg = (state, id) => hasUpgrade(state, id);
export const eff = (state) => effects(state);

// ---- field / plot geology for shale -------------------------------------------------------------------------
// Benches under a plot: the prospect's reservoirs (reservoirsAt); fringe plots outside every prospect still sit on the
// continuous shale — they get the first prospect's benches (dipped by distance) at tier 3.
export function benchesAt(fdef, plot) {
  const r = reservoirsAt(fdef, plot);
  if (r.length || !fdef?.shale) return r.filter((x) => !fdef?.shale || fdef.shale.benches.includes(x.id));
  const p = fdef.prospects?.[0];
  if (!p) return [];
  const m = plot.truth?.depthMult ?? 1;
  const dist = Math.hypot(plot.x - p.cx, plot.y - p.cy);
  return (p.reservoirs ?? []).filter((x) => fdef.shale.benches.includes(x.id)).map((x) => ({ ...x, top: Math.round((x.top + (x.dipFtPerPlot ?? 0) * dist) * m * 10) / 10, thick: x.thick ?? 15 }))
    .sort((a, b) => a.top - b.top);
}
export function tierOf(state, fid, plotIdx) {
  const fdef = fieldDef(fid);
  const plot = state.fields[fid]?.plots?.[plotIdx];
  if (!fdef || !plot) return 3;
  if (reservoirsAt(fdef, plot).length === 0) return 3;
  return plotTier(fdef, plotIdx);
}
export const tierMult = (tier) => TIER_QI_MULT[tier] ?? 0.3;
// Plots inside a reservoir's prospect (for the per-slot drainage volume). Memoized per field state object.
const PLOTS_MEMO = new WeakMap();
export function plotsInProspect(state, fid) {
  const f = state.fields[fid];
  let m = PLOTS_MEMO.get(f);
  if (!m) { m = Math.max(1, f.plots.filter((p) => (p.truth?.prospectIdx ?? -1) >= 0).length); PLOTS_MEMO.set(f, m); }
  return m;
}
export const resDefOf = (fid, rid) => { for (const p of fieldDef(fid)?.prospects ?? []) for (const r of p.reservoirs ?? []) if (r.id === rid) return r; return null; };
export const wellStreams = (well) => streamsOf(resDefOf(well.fieldId, well.reservoirId));
export const padKey = (fid, idx) => `${fid}:${idx}`;
export const baseDollars = (state, amt, by) => eraDollars(state, amt, by ?? 2014);
export const ok = (msg, extra = {}) => ({ ok: true, msg, ...extra });
export const no = (msg) => ({ ok: false, msg });
export const round = (x, n = 0) => { const k = 10 ** n; return Math.round(x * k) / k; };
